// treatment-use.component.ts
import { AfterViewInit, Component, Input, OnInit, ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { TCenter } from '@shared/interfaces/TCenter';
import { TProject } from '@shared/interfaces/TProject';
import { TreatmentCenter } from 'src/app/interfaces/treatment-used-table-data.interface';
import { PagingService } from 'src/app/services/paging.service';
import { KamReportsService } from '../../services/kam-reports.service';

@Component({
  selector: 'app-treatment-used',
  templateUrl: './treatment-used.component.html',
  styleUrls: ['./treatment-used.component.scss']
})
export class TreatmentUsedComponent implements OnInit, AfterViewInit {

  @Input() project: TProject;
  @ViewChild(MatSort) therapySort: MatSort;
  @ViewChild(MatPaginator) therapyPaginator: MatPaginator;

  dataSource: MatTableDataSource<any>;
  therapyTypes: string[];
  displayedColumns: string[];

  constructor(
    private _kamReportsService: KamReportsService,
    private _pagingService: PagingService,
  ) {}

  get centers(): TCenter[] { return this._kamReportsService.allCenters; }

  ngOnInit(): void {
    this.therapyTypes = this.project.treatment_options.map(option => option.name);
    this.displayedColumns = ['name', 'totalTherapiesUsed', ...this.therapyTypes.map(item => this.createKey(item))];
    const tableData = this.buildTreatmentData();
    this.dataSource = new MatTableDataSource(tableData);
  }

  ngAfterViewInit() {
    this.dataSource.sort = this.therapySort;
    this.dataSource.paginator = this.therapyPaginator;
  }

  buildTreatmentData(): any[] {

    // ? FIELDS
    // project > treatment_options
    // center > projectData > treatment_option_total_count
    // center > projectData > treatment_options_counts
    // center > projectData > treatment_options_validated

    // build TreatmentCenter[]
    let treatmentData: TreatmentCenter[] = [];

    this.centers.forEach(center => {
      const projectData = center.projectsData.filter(pd => pd.projectId === this.project.id)[0];
      if (projectData.treatment_option_total_count > 0) {
        treatmentData.push({
          name: center.c_name || center.c_original_name,
          total: projectData.treatment_option_total_count,
          items: this.therapyTypes.map((tType, index) => ({ name: tType, count: projectData.treatment_options_counts[index] || 0 })),
        });
      }
    });

    // average
    const average: TreatmentCenter = {
      name: 'National average',
      total: 0,
      items:  this.therapyTypes.map(tType => ({ name: tType, count: 0 })),
    }
    treatmentData.forEach(td => {
      average.total += td.total;
      td.items.forEach((item, index) => {
        average.items[index].count += item.count;
      });
    });
    average.total = Math.round(average.total / treatmentData.length);
    average.items.forEach(therapy => {
      therapy.count = Math.round(therapy.count / treatmentData.length);
    });
    // Add the average to the beginning of the treatmentData array
    treatmentData = [average, ...treatmentData];

    // convert TreatmentCenter to raw table data
    const result = treatmentData.map(center => {
      const rowData: any = {
        name: center.name,
        totalTherapiesUsed: center.total
      };
      // Add each therapy type as a separate column
      center.items.forEach(item => {
        rowData[this.createKey(item.name)] = item.count;
      });
      return rowData;
    });

    return result;
  }

  /**
   * Creates a key from a string by:
   * 1. Converting to lowercase
   * 2. Removing all whitespace
   * 3. Removing all non-letter characters
   *
   * @param input - The input string to convert to a key
   * @returns The normalized key string
   */
  createKey(input: string): string {
    return input
      .toLowerCase()            // Convert to lowercase
      .replace(/\s+/g, '')      // Remove all whitespace
      .replace(/[^a-z0-9]/g, '');  // Remove all non-letter characters
  }

  // get pagination size options
  getPageSizeOptions(length: number): number[] {
    return this._pagingService.getPageSizeOptions(length);
  }

}

